import { useState, useEffect, useMemo } from 'react';
import { staffAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Trash2, Search, Filter, X, LogIn, Pencil, MoreVertical, Eye, EyeOff } from 'lucide-react';
import { BulkSelectCheckbox, BulkActionBar } from '@/components/BulkActions';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';

const roleColors = {
  admin: 'bg-red-100 text-red-800 border border-red-300',
  sales: 'bg-blue-100 text-blue-800 border border-blue-300',
  finance: 'bg-green-100 text-green-800 border border-green-300',
  viewer: 'bg-gray-100 text-gray-700 border border-gray-300',
};

export default function AdminStaff() {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'sales', phone: '' });
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Search and filters
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selected, setSelected] = useState([]);
  const toggleSelect = (id) => setSelected(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

  const load = () => staffAPI.list().then(r => { setStaff(r.data); setLoading(false); }).catch(() => setLoading(false));
  useEffect(() => { load(); }, []);

  // Filtered staff
  const filteredStaff = useMemo(() => {
    return staff.filter(s => {
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch = 
          s.name?.toLowerCase().includes(query) ||
          s.email?.toLowerCase().includes(query) ||
          s.phone?.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }
      if (roleFilter !== 'all' && s.role !== roleFilter) return false;
      if (statusFilter !== 'all') {
        if (statusFilter === 'active' && !s.is_active) return false;
        if (statusFilter === 'inactive' && s.is_active) return false;
      }
      return true;
    });
  }, [staff, searchQuery, roleFilter, statusFilter]);

  const clearFilters = () => {
    setSearchQuery('');
    setRoleFilter('all');
    setStatusFilter('all');
  };

  const hasActiveFilters = searchQuery || roleFilter !== 'all' || statusFilter !== 'all';

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editMode && editId) {
        // Update existing staff
        const updateData = { name: form.name, role: form.role, phone: form.phone };
        if (form.password) updateData.password = form.password;
        await staffAPI.update(editId, updateData);
        toast.success('Staff member updated');
      } else {
        // Create new staff - credentials will be emailed
        await staffAPI.create({ ...form, user_type: 'staff' });
        toast.success('Staff member added. Credentials sent to email.');
      }
      setDialogOpen(false);
      resetForm();
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setSaving(false);
  };

  const resetForm = () => {
    setForm({ name: '', email: '', password: '', role: 'sales', phone: '' });
    setEditMode(false);
    setEditId(null);
    setShowPassword(false);
  };

  const openEdit = (s) => {
    setForm({ name: s.name || '', email: s.email || '', password: '', role: s.role || 'sales', phone: s.phone || '' });
    setEditMode(true);
    setEditId(s.id);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this staff member?')) return;
    try { await staffAPI.delete(id); toast.success('Removed'); load(); } catch { toast.error('Failed'); }
  };

  const handleToggleActive = async (s) => {
    try {
      await staffAPI.update(s.id, { is_active: !s.is_active });
      toast.success(s.is_active ? 'Staff deactivated' : 'Staff activated');
      load();
    } catch { toast.error('Failed to update status'); }
  };

  const handleLoginAsStaff = async (s) => {
    try {
      const res = await staffAPI.loginAs(s.id);
      const token = res.data.token;
      const staffInfo = res.data.staff;
      
      // Open impersonate page in new tab with token
      const encodedUser = encodeURIComponent(JSON.stringify(staffInfo));
      window.open(`/impersonate?token=${token}&user=${encodedUser}`, '_blank');
      
      toast.success(`Opening ${s.name}'s view...`);
    } catch (err) { 
      toast.error(err.response?.data?.detail || 'Failed to login as staff'); 
    }
  };

  return (
    <div data-testid="admin-staff">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <h1 className="text-2xl font-bold text-gray-900 uppercase">Staff Management</h1>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">{filteredStaff.length} of {staff.length} staff</span>
          <Button className="bg-[#E31937] text-white hover:bg-[#C4122C]" onClick={() => { resetForm(); setDialogOpen(true); }} data-testid="add-staff-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Staff
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input placeholder="Search by name, email, phone..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9 h-9 text-sm" data-testid="search-input" />
          </div>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-[130px] h-9 text-sm" data-testid="role-filter">
              <Filter className="w-3 h-3 mr-1.5 text-gray-400" /><SelectValue placeholder="Role" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
              <SelectItem value="sales">Sales</SelectItem>
              <SelectItem value="finance">Finance</SelectItem>
              <SelectItem value="viewer">Viewer</SelectItem>
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[130px] h-9 text-sm" data-testid="status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters} className="h-9 text-xs text-gray-500">
              <X className="w-3 h-3 mr-1" /> Clear
            </Button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-gray-200 hover:bg-transparent">
              <TableHead className="w-10"><BulkSelectCheckbox checked={selected.length === filteredStaff.length && filteredStaff.length > 0} onCheckedChange={() => setSelected(p => p.length === filteredStaff.length ? [] : filteredStaff.map(s => s.id))} testId="select-all-staff" /></TableHead>
              <TableHead className="text-gray-500">Name</TableHead>
              <TableHead className="text-gray-500 hidden sm:table-cell">Email</TableHead>
              <TableHead className="text-gray-500 hidden md:table-cell">Phone</TableHead>
              <TableHead className="text-gray-500">Role</TableHead>
              <TableHead className="text-gray-500 hidden sm:table-cell">Status</TableHead>
              <TableHead className="text-gray-500 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} className="text-center text-gray-400 py-8">Loading...</TableCell></TableRow>
            ) : filteredStaff.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center text-gray-400 py-8">{hasActiveFilters ? 'No staff match your filters' : 'No staff members yet'}</TableCell></TableRow>
            ) : filteredStaff.map(s => (
              <TableRow key={s.id} className="border-gray-200 text-gray-700">
                <TableCell><BulkSelectCheckbox checked={selected.includes(s.id)} onCheckedChange={() => toggleSelect(s.id)} testId={`select-staff-${s.id}`} /></TableCell>
                <TableCell className="font-medium">{s.name}</TableCell>
                <TableCell className="text-xs hidden sm:table-cell">{s.email}</TableCell>
                <TableCell className="text-xs hidden md:table-cell">{s.phone || '--'}</TableCell>
                <TableCell><Badge className={`${roleColors[s.role] || roleColors.viewer} text-xs capitalize`}>{s.role}</Badge></TableCell>
                <TableCell className="hidden sm:table-cell">
                  <Badge 
                    className={`${s.is_active ? 'bg-green-100 text-green-800 border border-green-300' : 'bg-red-100 text-red-800 border border-red-300'} cursor-pointer`}
                    onClick={() => handleToggleActive(s)}
                  >
                    {s.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  {/* Desktop actions */}
                  <div className="hidden sm:flex items-center justify-end gap-1">
                    {s.role !== 'admin' && (
                      <Button variant="ghost" size="sm" className="h-7 px-2 text-blue-600" onClick={() => handleLoginAsStaff(s)} title="Login as this staff" data-testid={`login-as-${s.id}`}>
                        <LogIn className="w-3 h-3 mr-1" /> View
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEdit(s)} data-testid={`edit-${s.id}`}>
                      <Pencil className="w-3 h-3" />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400" onClick={() => handleDelete(s.id)}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                  {/* Mobile dropdown */}
                  <div className="sm:hidden">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0"><MoreVertical className="w-4 h-4" /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-white">
                        {s.role !== 'admin' && (
                          <DropdownMenuItem onClick={() => handleLoginAsStaff(s)}><LogIn className="w-3.5 h-3.5 mr-2 text-blue-600" />Login As</DropdownMenuItem>
                        )}
                        <DropdownMenuItem onClick={() => openEdit(s)}><Pencil className="w-3.5 h-3.5 mr-2" />Edit</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleToggleActive(s)}>{s.is_active ? 'Deactivate' : 'Activate'}</DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => handleDelete(s.id)} className="text-red-500"><Trash2 className="w-3.5 h-3.5 mr-2" />Delete</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetForm(); }}>
        <DialogContent className="bg-white border-gray-200 text-gray-900" data-testid="staff-dialog">
          <DialogHeader><DialogTitle className="text-gray-900 uppercase">{editMode ? 'Edit Staff Member' : 'Add Staff Member'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSave} className="space-y-3">
            <Input placeholder="Full Name *" required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="bg-gray-100 border-gray-200" data-testid="staff-name" />
            <Input 
              placeholder="Email *" 
              type="email" 
              required={!editMode}
              disabled={editMode}
              value={form.email} 
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))} 
              className={`bg-gray-100 border-gray-200 ${editMode ? 'opacity-50' : ''}`} 
              data-testid="staff-email" 
            />
            <div>
              <label className="text-xs text-gray-500 mb-1 block">
                {editMode ? 'New Password (leave blank to keep current)' : 'Password *'}
              </label>
              <div className="relative">
                <Input 
                  placeholder={editMode ? "Enter new password to change" : "Enter password"}
                  type={showPassword ? "text" : "password"} 
                  required={!editMode}
                  value={form.password} 
                  onChange={e => setForm(f => ({ ...f, password: e.target.value }))} 
                  className="bg-gray-100 border-gray-200 pr-10" 
                  data-testid="staff-password" 
                />
                <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {editMode && (
                <p className="text-xs text-gray-400 mt-1">Leave blank to keep the existing password.</p>
              )}
            </div>
            <Input placeholder="Phone" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className="bg-gray-100 border-gray-200" />
            <Select value={form.role} onValueChange={v => setForm(f => ({ ...f, role: v }))}>
              <SelectTrigger className="bg-gray-100 border-gray-200" data-testid="staff-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="sales">Sales</SelectItem>
                <SelectItem value="finance">Finance</SelectItem>
                <SelectItem value="viewer">Viewer</SelectItem>
              </SelectContent>
            </Select>
            {!editMode && (
              <p className="text-xs text-gray-500">Login credentials will be sent to the staff member's email.</p>
            )}
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" className="border-gray-300" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-[#E31937] text-white" disabled={saving} data-testid="save-staff-btn">{saving ? 'Saving...' : editMode ? 'Update' : 'Add Staff'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <BulkActionBar count={selected.length} onDelete={async () => {
        if (!window.confirm(`Delete ${selected.length} staff member(s)?`)) return;
        try { await staffAPI.bulkDelete(selected); toast.success(`${selected.length} staff member(s) deleted`); setSelected([]); load(); } catch { toast.error('Failed'); }
      }} onClear={() => setSelected([])} label="staff" />
    </div>
  );
}
